import {
  ContentUnderstandingMediaKind,
  MemCommonEditorContentUnderstandingExtractionStatus,
  MemCommonEditorContentUnderstandingIndexingStatus,
  MemCommonEditorContentUnderstandingInfo,
  deriveFileContentUnderstandingState,
} from "@mem-labs/common-editor";
import { validate as validateUuid } from "uuid";
import { NoteAttachmentProcessingStatus, ProcessingInfo } from "@/store/note-attachments/types";

const UUID_REGEX = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/**
 * Extracts the file_id (client-generated UUID) from a SpaceAccountNoteAttachment's attachment_url.
 *
 * The attachment_url is a proxy URL like:
 *   https://www.mem.storage/proxy?url=<encoded-gcs-url>&token=<uuid>
 *
 * The inner GCS URL contains the file_id as a path segment (the blob name includes
 * {spaceAccountId}/{fileId}/{filename}). We decode the inner `url` param and extract
 * the first UUID from its path, which corresponds to the client-generated file_id
 * stored as the TipTap file node's `fileId` attribute.
 */
export const extractFileIdFromAttachmentUrl = (attachmentUrl: string): string | null => {
  try {
    const parsed = new URL(attachmentUrl);
    const innerUrl = parsed.searchParams.get("url");
    if (innerUrl) {
      // Extract UUIDs from the decoded inner URL path only (not query params)
      const innerParsed = new URL(innerUrl);
      const pathMatches = innerParsed.pathname.match(UUID_REGEX);
      if (pathMatches) {
        const candidate = pathMatches[0];
        if (validateUuid(candidate)) return candidate;
      }
    }
  } catch {
    // Fallback: not a proxy URL, try direct extraction
  }

  // Fallback for non-proxy URLs: use the first valid UUID found
  const matches = attachmentUrl.match(UUID_REGEX);
  if (matches) {
    for (const candidate of matches) {
      if (validateUuid(candidate)) return candidate;
    }
  }
  return null;
};

export const mapContentUnderstandingState = ({
  extractionStatus,
  indexingStatus,
  processingInfo,
  mediaKind,
  suggestedSearch,
  ocrMarkdown,
  includeDisplayState,
}: {
  extractionStatus: NoteAttachmentProcessingStatus | null;
  indexingStatus: NoteAttachmentProcessingStatus | null;
  processingInfo: ProcessingInfo | null;
  mediaKind?: ContentUnderstandingMediaKind;
  suggestedSearch?: string | null;
  ocrMarkdown?: string | null;
  includeDisplayState?: boolean;
}): MemCommonEditorContentUnderstandingInfo | undefined => {
  if (!extractionStatus || !indexingStatus) {
    return undefined;
  }

  return deriveFileContentUnderstandingState({
    extractionStatus:
      extractionStatus as unknown as MemCommonEditorContentUnderstandingExtractionStatus,
    indexingStatus: indexingStatus as unknown as MemCommonEditorContentUnderstandingIndexingStatus,
    processingInfo: processingInfo
      ? {
          pagesProcessed: processingInfo.pages_processed,
          pagesTotal: processingInfo.pages_total,
          pagesFailed: processingInfo.pages_failed,
          wasCutoff: processingInfo.was_cutoff,
          cutoffLimit: processingInfo.cutoff_limit,
        }
      : null,
    mediaKind,
    suggestedSearch,
    ocrMarkdown,
    includeDisplayState,
  });
};
